<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationList from './ConversationList.vue';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';

const props = defineProps({
  mineConversations: { type: Array, default: () => [] },
  unassignedConversations: { type: Array, default: () => [] },
  mineCount: { type: Number, default: 0 },
  unassignedCount: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
  mineEndReached: { type: Boolean, default: false },
  unassignedEndReached: { type: Boolean, default: false },
  activeStatus: { type: String, default: 'open' },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  isOnExpandedLayout: { type: Boolean, default: false },
});
const emit = defineEmits(['loadMore']);
const { t } = useI18n();
const listRef = ref(null);
useChatListKeyboardEvents(listRef);

const sections = computed(() => {
  const items = [
    {
      key: 'me',
      title:
        props.activeStatus === 'open'
          ? t('CHAT_LIST.SECTIONS.ACTIVE')
          : t('CHAT_LIST.SECTIONS.MINE'),
      conversations: props.mineConversations,
      count: props.mineCount,
      endReached: props.mineEndReached,
    },
  ];
  if (
    props.unassignedConversations.length ||
    (!props.unassignedEndReached && props.unassignedCount > 0)
  ) {
    items.push({
      key: 'unassigned',
      title: t('CHAT_LIST.SECTIONS.UNASSIGNED'),
      conversations: props.unassignedConversations,
      count: props.unassignedCount,
      endReached: props.unassignedEndReached,
    });
  }
  return items;
});
</script>

<template>
  <div ref="listRef" class="flex flex-col flex-1 min-h-0">
    <section
      v-for="section in sections"
      :key="section.key"
      :data-conversation-section="section.key"
      :aria-label="section.title"
      class="flex flex-col min-h-0"
      :class="[
        section.key === 'unassigned'
          ? 'border-t border-n-weak flex-[2]'
          : 'flex-[3]',
        !section.conversations.length && !isLoading && '!flex-none',
      ]"
    >
      <header class="flex shrink-0 items-center gap-2 px-4 py-2.5 bg-n-slate-2">
        <span
          class="size-1.5 rounded-full"
          :class="section.key === 'me' ? 'bg-n-brand' : 'bg-n-slate-9'"
        />
        <h2 class="text-xs font-semibold text-n-slate-11">
          {{ section.title }}
        </h2>
        <span class="text-xs tabular-nums text-n-slate-10">
          {{ section.count }}
        </span>
      </header>
      <p
        v-if="
          section.key === 'me' && !section.conversations.length && !isLoading
        "
        class="px-4 py-5 text-sm text-center text-n-slate-11"
      >
        {{ $t('CHAT_LIST.SECTIONS.EMPTY') }}
      </p>
      <ConversationList
        v-else
        :conversation-list="section.conversations"
        :enable-keyboard-navigation="false"
        :is-loading="isLoading"
        :show-end-of-list-message="section.endReached"
        :label="label"
        :team-id="teamId"
        :conversation-type="conversationType"
        :is-on-expanded-layout="isOnExpandedLayout"
        @load-more="emit('loadMore', section.key)"
      />
    </section>
  </div>
</template>
